import { logger } from './debug/logger.js';

/**
 * Cache global pour les imports dynamiques
 * Évite de réimporter les mêmes modules plusieurs fois
 */
class ModuleCache {
    constructor() {
        this.cache = new Map();
        this.loadingPromises = new Map();
    }

    /**
     * Importe un module avec mise en cache
     * @param {string} modulePath - Chemin du module à importer
     * @returns {Promise<any>} Module importé
     */
    async import(modulePath) {
        // Si le module est déjà en cache, le retourner immédiatement
        if (this.cache.has(modulePath)) {
            logger.cache.debug(`🔄 Module en cache: ${modulePath}`);
            return this.cache.get(modulePath);
        }

        // Si le module est en cours de chargement, attendre la promesse existante
        if (this.loadingPromises.has(modulePath)) {
            logger.cache.debug(`⏳ Module en cours de chargement: ${modulePath}`);
            return this.loadingPromises.get(modulePath);
        }

        // Démarrer un nouveau chargement
        logger.cache.info(`📦 Nouveau chargement: ${modulePath}`);
        const loadingPromise = this._loadModule(modulePath);
        
        // Stocker la promesse pour éviter les chargements simultanés
        this.loadingPromises.set(modulePath, loadingPromise);

        try {
            const module = await loadingPromise;
            
            // Mettre en cache le module chargé
            this.cache.set(modulePath, module);
            
            // Nettoyer la promesse de chargement
            this.loadingPromises.delete(modulePath);
            
            logger.cache.info(`✅ Module mis en cache: ${modulePath}`);
            return module;
        } catch (error) {
            // En cas d'erreur, nettoyer la promesse de chargement
            this.loadingPromises.delete(modulePath);
            logger.cache.error(`❌ Erreur lors du chargement de ${modulePath}:`, error);
            throw error;
        }
    }

    /**
     * Charge effectivement le module
     * @private
     */
    async _loadModule(modulePath) {
        // Utiliser une fonction factory pour éviter les warnings webpack
        const importModule = new Function('path', 'return import(path)');
        return await importModule(modulePath);
    }

    /**
     * Vérifie si un module est en cache
     * @param {string} modulePath 
     * @returns {boolean}
     */
    has(modulePath) {
        return this.cache.has(modulePath);
    }

    /**
     * Accès direct au cache pour usage avancé
     * @returns {Map}
     */
    getCache() {
        return this.cache;
    }

    /**
     * Vide le cache (utile pour le développement)
     */
    clear() {
        this.cache.clear();
        this.loadingPromises.clear();
        logger.cache.info('🧹 Cache des modules vidé');
    }

    /**
     * Obtient des statistiques sur le cache
     * @returns {Object}
     */
    getStats() {
        return {
            cachedModules: this.cache.size,
            loadingModules: this.loadingPromises.size,
            modules: Array.from(this.cache.keys())
        };
    }

    /**
     * Preload des modules critiques
     * @param {string[]} modulePaths 
     */
    async preload(modulePaths) {
        logger.cache.info('🔄 Préchargement des modules critiques:', modulePaths);
        
        const preloadPromises = modulePaths.map(path => 
            this.import(path).catch(error => {
                logger.cache.warn(`⚠️ Échec du préchargement de ${path}:`, error);
                return null;
            })
        );
        
        await Promise.allSettled(preloadPromises);
        logger.cache.info('✅ Préchargement terminé');
    }
}

// Instance globale
const moduleCache = new ModuleCache();

// Rendre accessible pour le débogage
if (typeof window !== 'undefined') {
    window.moduleCache = moduleCache;
}

export default moduleCache;